import { memo, useCallback, useMemo, useState } from "react"
import { Box, IconButton, List, ListItem, Tooltip, useMediaQuery } from "@material-ui/core"
import DeleteIcon from "@material-ui/icons/Delete"
import ExpandLessIcon from "@material-ui/icons/ExpandLess"
import ExpandMoreIcon from "@material-ui/icons/ExpandMore"
import SpeedIcon from "@material-ui/icons/Speed"
import VisibilityIcon from "@material-ui/icons/Visibility"
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing"
import ReportProblemIcon from "@mui/icons-material/ReportProblem"
import WifiIcon from "@mui/icons-material/Wifi"
import {
  stateTreeActionButtonStyle,
  stateTreeBadgeStyle,
  stateTreeCardBaseStyle,
  stateTreeGuideColor,
  stateTreeNestedListStyle,
  themePalette,
} from "./stateTreeStyles"

const MAX_ACCEPTABLE_INTEGRITY = 90
const MAX_ACCEPTABLE_DBM_WIFI = -65
const MAX_ACCEPTABLE_DBM_MOBILE = -80
const SELECTED_NODE_BACKGROUND = "rgb(239, 246, 255)"

// Normaliza etiquetas de nivel para comparar sin depender de acentos o variantes del backend.
const normalizeNodeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()

// Resuelve el nombre visible del nodo sin acoplar la vista al shape exacto de cada variante.
const getNodeDisplayName = (node) => node?.nombre || node?.device_info?.device_name || "-"

// Formatea la jerarquia para que el badge sea mas legible visualmente.
const formatHierarchyBadgeLabel = (value) =>
  String(value || "")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")

// Devuelve la jerarquia visible del nodo para priorizar el nivel del arbol por sobre el producto.
const getNodeBadgeLabel = (node) => {
  if (node?.level_nombre) return formatHierarchyBadgeLabel(node.level_nombre)
  if (node?.type === "automate") return "Automate"
  if (node?.producto || node?.product) return formatHierarchyBadgeLabel(node.producto || node.product)
  return "Nodo"
}

// Conserva la regla actual de seleccion para no habilitar nodos que hoy siguen siendo solo estructurales.
const shouldDisableNodeSelection = (node, fromStateDashboard) =>
  (!node?.medidor && !(node?.automates?.length > 0)) &&
  fromStateDashboard &&
  node?.type !== "automate"

// Agrupa las alertas del nodo con la misma logica actual para mantener tooltips e indicadores.
const buildNodeAlertMessages = (nodeStatus) => {
  const messages = []
  const integrity = parseFloat(nodeStatus?.powermate?.data_integrity?.replace("%", "")) || 100

  if (integrity < MAX_ACCEPTABLE_INTEGRITY) {
    messages.push(`Baja integridad de datos (${nodeStatus?.powermate?.data_integrity})`)
  }
  if (nodeStatus?.wifi?.dbm < MAX_ACCEPTABLE_DBM_WIFI) {
    messages.push(`Baja intensidad de senal WiFi (${nodeStatus?.wifi?.dbm} dBm)`)
  }
  if (nodeStatus?.mobile?.dbm < MAX_ACCEPTABLE_DBM_MOBILE) {
    messages.push(`Baja intensidad de senal Mobile (${nodeStatus?.mobile?.dbm} dBm)`)
  }

  return messages
}

// Ajusta margenes y fondos segun profundidad para que el arbol siga leyendo como tarjetas anidadas.
const getNodeRowStyle = ({ isSelected, isDisabled, depth }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.75rem",
  padding: depth === 0 ? "0.42rem 0.75rem" : "0.36rem 0.72rem",
  minHeight: depth === 0 ? "48px" : "44px",
  cursor: isDisabled ? "default" : "pointer",
  backgroundColor: isSelected
    ? SELECTED_NODE_BACKGROUND
    : themePalette.common.white,
  opacity: isDisabled ? 0.64 : 1,
  boxSizing: "border-box",
})

// Define la tarjeta visual individual del nodo. El borde vive aqui y no en el contenedor de la rama.
const getNodeItemStyle = ({ isSelected, depth, hasChildren }) => ({
  ...stateTreeCardBaseStyle,
  boxShadow: depth === 0 ? themePalette.shadow.card : "none",
  borderColor: isSelected ? "rgb(37 134 188 / 26%)" : themePalette.border.subtle,
  backgroundColor: isSelected
    ? SELECTED_NODE_BACKGROUND
    : hasChildren && depth > 0
      ? themePalette.surface.soft
      : themePalette.common.white,
  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
})

// Dibuja los conectores del arbol sin alterar la posicion interna de los nodos.
const buildTreeGuideStyle = ({ isCompact, type }) => {
  if (type === "top-vertical") {
    return {
      position: "absolute",
      left: isCompact ? "-0.58rem" : "-0.67rem",
      top: "-0.45rem",
      height: "calc(50% + 0.45rem)",
      width: "1px",
      backgroundColor: stateTreeGuideColor,
      pointerEvents: "none",
    }
  }

  if (type === "bottom-vertical") {
    return {
      position: "absolute",
      left: isCompact ? "-0.58rem" : "-0.67rem",
      top: "50%",
      height: "calc(50% + 0.45rem)",
      width: "1px",
      backgroundColor: stateTreeGuideColor,
      pointerEvents: "none",
    }
  }

  return {
    position: "absolute",
    left: isCompact ? "-0.72rem" : "-0.82rem",
    top: "50%",
    width: isCompact ? "0.72rem" : "0.82rem",
    height: "1px",
    backgroundColor: stateTreeGuideColor,
    transform: "translateY(-50%)",
    pointerEvents: "none",
  }
}

const EMPTY_CHILDREN = []

function StateNode({
  statusNodes,
  statusNodesByNodeId,
  node,
  selectedNode,
  selectedNodeId,
  selectedPathIds,
  handleSelectedNode,
  status,
  statusByNodeId,
  fromStateDashboard = true,
  handleDeleteNode,
  depth = 0,
  isCompactView,
  preserveCollapsedChildren = true,
}) {
  const [open, setOpen] = useState(true)
  const mediaCompact = useMediaQuery("(max-width:720px)")
  const isCompact = isCompactView ?? mediaCompact

  const childNodes = node?.hijos || EMPTY_CHILDREN
  const hasChildren = childNodes.length > 0
  const resolvedSelectedNodeId = selectedNodeId ?? selectedNode?.id ?? null
  const isSelected = resolvedSelectedNodeId === node?.id
  const isDisabled = shouldDisableNodeSelection(node, fromStateDashboard)
  const levelName = useMemo(() => normalizeNodeText(node?.level_nombre), [node?.level_nombre])
  const nodeLabel = useMemo(() => getNodeDisplayName(node), [node])
  const badgeLabel = useMemo(() => getNodeBadgeLabel(node), [node])

  // Resuelve el estado del nodo con acceso O(1) cuando el contenedor ya lo indexo por id.
  const nodeStatus = useMemo(() => {
    if (statusByNodeId) return statusByNodeId[node?.id]
    return status?.find((currentNode) => currentNode.node_id === node?.id)
  }, [node?.id, status, statusByNodeId])

  // Resuelve conectividad del medidor con mapa preindexado para evitar busquedas repetidas por nodo.
  const nodeConnectivity = useMemo(() => {
    if (statusNodesByNodeId) return statusNodesByNodeId[node?.id]
    return statusNodes?.find((statusItem) => statusItem.node_id === node?.id)
  }, [node?.id, statusNodes, statusNodesByNodeId])

  const statusNode = node?.virtual ? undefined : nodeConnectivity?.online
  const alertMessages = useMemo(() => buildNodeAlertMessages(nodeStatus), [nodeStatus])
  const rowStyle = useMemo(() => {
    const baseRowStyle = getNodeRowStyle({ isSelected, isDisabled, depth })
    return {
      ...baseRowStyle,
      alignItems: isCompact ? "flex-start" : "center",
      padding: isCompact ? "0.38rem 0.55rem" : baseRowStyle.padding,
    }
  }, [depth, isCompact, isDisabled, isSelected])

  const nodeItemStyle = useMemo(
    () => ({
      ...getNodeItemStyle({ isSelected, depth, hasChildren }),
      ...rowStyle,
    }),
    [depth, hasChildren, isSelected, rowStyle]
  )

  const nodeContainerStyle = useMemo(
    () => ({
      listStyle: "none",
      width: "100%",
      boxSizing: "border-box",
      marginBottom: depth === 0 ? "0.65rem" : 0,
      position: "relative",
      overflow: "visible",
      borderRadius: stateTreeCardBaseStyle.borderRadius,
      backgroundColor: "transparent",
      boxShadow: "none",
      transition: "none",
    }),
    [depth, isSelected]
  )

  const horizontalGuideStyle = useMemo(
    () => (depth > 0 ? buildTreeGuideStyle({ isCompact, type: "horizontal" }) : null),
    [depth, isCompact]
  )

  const handleArrows = useCallback((event) => {
    event.stopPropagation()
    setOpen((currentOpen) => !currentOpen)
  }, [])

  // Mantiene bloqueada la seleccion de nodos estructurales, pero ya no bloquea sus acciones hijas.
  const handleRowClick = useCallback(() => {
    if (isDisabled) return
    handleSelectedNode(node)
  }, [handleSelectedNode, isDisabled, node])

  const childListStyle = useMemo(
    () => ({
      display: "flex",
      flexDirection: "column",
      gap: "0.45rem",
      margin: 0,
      width: "100%",
    }),
    []
  )

  const shouldRenderChildren = preserveCollapsedChildren || open

  return (
    <Box
      component="li"
      style={nodeContainerStyle}
    >
      {horizontalGuideStyle ? <Box aria-hidden style={horizontalGuideStyle} /> : null}

      <ListItem
        component="div"
        button
        selected={isSelected}
        onClick={handleRowClick}
        aria-disabled={isDisabled}
        style={nodeItemStyle}
      >
        <Box
          style={{
            minWidth: 0,
            flex: 1,
            display: "flex",
            alignItems: isCompact ? "flex-start" : "center",
            gap: isCompact ? "0.4rem" : "0.55rem",
            overflow: "hidden",
          }}
        >
          {!node?.owner && (
            <Tooltip title={`El nodo ${nodeLabel} es compartido`}>
              <IconButton
                edge="end"
                aria-label="nodo compartido"
                size="small"
                style={{ ...stateTreeActionButtonStyle, padding: 0 }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Box
            style={{
              flexShrink: 0,
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: node?.color || themePalette.primary.main,
              border: `2px solid ${themePalette.common.white}`,
              boxShadow: `0 0 0 1px ${themePalette.border.main}, 0 3px 8px rgba(15, 23, 42, 0.08)`,
            }}
          />

          <span
            style={{
              ...stateTreeBadgeStyle,
              fontSize: isCompact ? "0.68rem" : stateTreeBadgeStyle.fontSize,
              padding: isCompact ? "0.1rem 0.35rem" : stateTreeBadgeStyle.padding,
              backgroundColor:
                levelName === "empresa" ? "rgba(8, 95, 145, 0.08)" : stateTreeBadgeStyle.backgroundColor,
              borderColor:
                levelName === "empresa" ? "rgba(8, 95, 145, 0.18)" : stateTreeBadgeStyle.borderColor,
              color: levelName === "empresa" ? themePalette.text.section : stateTreeBadgeStyle.color,
            }}
          >
            {badgeLabel}
          </span>

          <Box style={{ minWidth: 0, flex: 1, display: "flex", alignItems: "baseline", gap: "0.35rem", flexWrap: "wrap", overflow: "hidden" }}>
            <span
              style={{
                fontSize: isCompact ? "0.88rem" : depth === 0 ? "1rem" : "0.96rem",
                fontWeight: depth === 0 ? 500 : 400,
                color: themePalette.primary.main,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {nodeLabel}
            </span>
          </Box>
        </Box>

        <Box
          style={{
            display: "flex",
            alignItems: "center",
            gap: isCompact ? "0.25rem" : "0.45rem",
            flexShrink: 0,
            paddingTop: isCompact ? 2 : 0,
          }}
        >
          {alertMessages.length > 0 && (
            <Tooltip title={alertMessages.join(" | ")}>
              <IconButton
                edge="end"
                aria-label="el medidor tiene alertas"
                size="small"
                style={{ padding: 4, color: themePalette.warning.main }}
              >
                <ReportProblemIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {nodeStatus?.rssi === "-10dB" && (
            <Tooltip title={`${nodeLabel} tiene excelente senal de WiFi`}>
              <IconButton
                edge="end"
                aria-label="la senal de wifi es excelente"
                size="small"
                style={{ padding: 4, color: themePalette.primary.main }}
              >
                <WifiIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {node?.medidor ? (
            <Tooltip title={`${nodeLabel} tiene un medidor asociado ${nodeStatus?.online ? "online" : "offline"}`}>
              <IconButton edge="end" aria-label="medidores asociados" size="small" style={{ padding: 4 }}>
                <SpeedIcon
                  fontSize="small"
                  style={{ color: statusNode ? themePalette.status.online : themePalette.status.offline }}
                />
              </IconButton>
            </Tooltip>
          ) : null}

          {node?.automates?.length > 0 && (
            <Tooltip title={`${nodeLabel} tiene un automate asociado`}>
              <IconButton edge="end" aria-label="automates asociados" size="small" style={{ padding: 4 }}>
                <PrecisionManufacturingIcon
                  fontSize="small"
                  style={{ color: themePalette.text.disabled }}
                />
              </IconButton>
            </Tooltip>
          )}

          {!fromStateDashboard && (
            <IconButton
              aria-label="eliminar franja horaria"
              onClick={() => handleDeleteNode?.(node)}
              size="small"
              style={{ padding: 4, color: themePalette.error.main }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}

          {hasChildren &&
            (open ? (
              <IconButton edge="end" aria-label="contraer" onClick={handleArrows} size="small" style={{ padding: 4 }}>
                <ExpandLessIcon fontSize="small" />
              </IconButton>
            ) : (
              <IconButton edge="end" aria-label="expandir" onClick={handleArrows} size="small" style={{ padding: 4 }}>
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            ))}
        </Box>
      </ListItem>

      {hasChildren && shouldRenderChildren && (
        <Box
          style={{
            ...stateTreeNestedListStyle,
            display: open ? "block" : "none",
          }}
        >
          <List disablePadding style={childListStyle}>
            {childNodes.map((childNode, index) => (
              <Box key={childNode.id} style={{ position: "relative" }}>
                <Box aria-hidden style={buildTreeGuideStyle({ isCompact, type: "top-vertical" })} />
                {index !== childNodes.length - 1 && <Box aria-hidden style={buildTreeGuideStyle({ isCompact, type: "bottom-vertical" })} />}
                <MemoStateNode
                  statusNodes={statusNodes}
                  statusNodesByNodeId={statusNodesByNodeId}
                  node={childNode}
                  handleSelectedNode={handleSelectedNode}
                  selectedNode={selectedNode}
                  selectedNodeId={resolvedSelectedNodeId}
                  selectedPathIds={selectedPathIds}
                  status={status}
                  statusByNodeId={statusByNodeId}
                  fromStateDashboard={fromStateDashboard}
                  handleDeleteNode={handleDeleteNode}
                  depth={depth + 1}
                  isCompactView={isCompact}
                  preserveCollapsedChildren={preserveCollapsedChildren}
                />
              </Box>
            ))}
          </List>
        </Box>
      )}
    </Box>
  )
}

const areNodePropsEqual = (prevProps, nextProps) => {
  const previousSelectedId = prevProps.selectedNodeId ?? prevProps.selectedNode?.id ?? null
  const nextSelectedId = nextProps.selectedNodeId ?? nextProps.selectedNode?.id ?? null
  const nodeId = prevProps.node?.id
  const previousPathHasNode = prevProps.selectedPathIds?.has(nodeId) ?? false
  const nextPathHasNode = nextProps.selectedPathIds?.has(nodeId) ?? false

  return (
    prevProps.node === nextProps.node &&
    prevProps.depth === nextProps.depth &&
    prevProps.fromStateDashboard === nextProps.fromStateDashboard &&
    prevProps.handleSelectedNode === nextProps.handleSelectedNode &&
    prevProps.handleDeleteNode === nextProps.handleDeleteNode &&
    prevProps.status === nextProps.status &&
    prevProps.statusByNodeId === nextProps.statusByNodeId &&
    prevProps.statusNodes === nextProps.statusNodes &&
    prevProps.statusNodesByNodeId === nextProps.statusNodesByNodeId &&
    prevProps.isCompactView === nextProps.isCompactView &&
    prevProps.preserveCollapsedChildren === nextProps.preserveCollapsedChildren &&
    previousSelectedId === nextSelectedId &&
    previousPathHasNode === nextPathHasNode
  )
}

const MemoStateNode = memo(StateNode, areNodePropsEqual)

export default MemoStateNode
